import { computed, ref } from 'vue';
import { useLiveQueryV2, CODES, UseLiveQueryConfigV2 } from '@libs/use';
import { CreateOrderItem } from '@libs/api-reference';
import {
  useUser,
  createUserChannelName,
  createNewOmsUserChannelName,
} from '@core/user';
import {
  SYMBOL_TRADE_CHANNEL_PREFIX,
  transformTradePriceChange,
} from '@core/symbol';
import { omitBy, sleep } from '@libs/utils';

import * as API from '../requests';
import {
  transformNewOmsOrderAndTradeChange,
  transformOrderAndTradeChange,
} from '../utils/transform/orderTrade';
import {
  ALGORITHM_ORDER_TYPE,
  LEGACY_ORDER_STATUSES,
  NEW_OMS_ORDER_STATUSES,
  ORDERS_QUERY_KEYS,
  ORDER_SOCKET_ERROR_DESCRIPTION,
} from '../constants';
import { updateTradePrice } from '../utils/updateTradePrice';
import useLiveOrderQuery from './useLiveOrderQuery';
import { isNewOms } from '../index';
import { transformOrderLabel } from '../utils/transform/orderHistory';

type UseLiveOrdersQueryConfigs = {
  onUpdateCallback?: UseLiveQueryConfigV2<any>['onUpdate'];
};

type UpdateOrderStatusParamsType = { orderId: string | number; type: string };

export default function useLiveOrdersQuery({
  onUpdateCallback,
}: UseLiveOrdersQueryConfigs = {}) {
  const orderId = ref<string>();

  const { user, isUserReady } = useUser();

  const isErrorOrderOnSocket = ref(false);

  const {
    result,
    execute,
    isRefetching: isWaitingForOrders,
    isPending: isLoadingOrders,
    isError: isErrorLoadingOrders,
    isSuccess: isSuccessLoadingOrders,
  } = useLiveQueryV2({
    immediate: isUserReady,
    staleTime: 3000,
    fetcher: API.getOrders,
    key: ORDERS_QUERY_KEYS.orders,
    getChannels: (result) => {
      let symbolChannels: string[] = [];

      if (result?.data?.items) {
        symbolChannels = result.data.items.reduce(
          (channels: string[], order) => {
            channels.push(
              `${SYMBOL_TRADE_CHANNEL_PREFIX}${order.instrumentIdentification}`
            );

            return channels;
          },
          []
        );
      }

      symbolChannels.push(createUserChannelName(user.value?.userName));
      symbolChannels.push(createNewOmsUserChannelName(user.value?.userId));

      return symbolChannels;
    },
    unsubscribe: false,
    onSuccess(result) {
      if (!result) {
        result = {
          data: {
            items: [],
          },
        };
      } else if (!result?.data?.items) {
        result.data.items = [];
      }
    },
    onUpdate(signal) {
      const actions = {
        [CODES.SymbolTradePriceChangeSignal]: transformTradePriceChange,
        [CODES.OmsOrderAndTradeSignal]: transformOrderAndTradeChange,
        [CODES.NewOmsOrderAndTradeSignal]: transformNewOmsOrderAndTradeChange,
      };

      if (actions[signal.code]) {
        const data = omitBy(
          actions[signal.code](signal.data),
          (v) => v === null || v === undefined
        );

        onUpdateCallback?.({ code: signal.code, data });

        if (result.value?.data?.items) {
          if (signal.code === CODES.SymbolTradePriceChangeSignal) {
            updateTradePrice(result.value?.data.items, data);
          } else if (
            signal.code === CODES.OmsOrderAndTradeSignal &&
            data.investorBourseCodeId ===
              user.value?.investorBourseCodes[0].id &&
            !data.isAlgorithm
          ) {
            if (data.messageType === 'sendOrder') {
              isErrorOrderOnSocket.value =
                ORDER_SOCKET_ERROR_DESCRIPTION.includes(data.description);
            }

            if (data.messageType === 'editOrder') {
              const editIndex = result.value.data.items.findIndex(
                (s) => s.orderId === data.parentOrderId
              );

              if (editIndex > -1) {
                const item = result.value.data.items[editIndex];

                if (item.dateOfEvent) {
                  const resultTime = new Date(item.dateOfEvent).getTime();
                  const socketTime = new Date(data.dateOfEvent).getTime();

                  if (resultTime > socketTime) {
                    return;
                  }
                }

                Object.assign(result.value.data.items[editIndex], {
                  ...result.value.data.items[editIndex],
                  orderStateId: data.parentOrderStateId,
                  orderStateEnglishTitle: data.parentOrderState,
                  dateOfEvent: data.dateOfEvent,
                });
              }
            }

            const index = result.value.data.items.findIndex(
              (s) => s.orderId === data.orderId
            );

            if (index > -1) {
              const item = result.value.data.items[index];

              if (item.dateOfEvent) {
                const resultTime = new Date(item.dateOfEvent).getTime();
                const socketTime = new Date(data.dateOfEvent).getTime();

                if (resultTime > socketTime) {
                  return;
                }
              }

              result.value.data.items.splice(index, 1, {
                ...result.value?.data.items[index],
                ...data,
                lastTradePrice:
                  result.value?.data.items[index].lastTradePrice ??
                  data.lastTradePrice,
              });
            } else {
              result.value.data.items.unshift(data);

              // Set the new order id to get the correct lastTradePrice
              orderId.value = data.orderId;
            }
          } else if (
            signal.code === CODES.NewOmsOrderAndTradeSignal &&
            !data.isAlgorithm
          ) {
            if (data.messageType === 'ErrorOrder') {
              isErrorOrderOnSocket.value =
                ORDER_SOCKET_ERROR_DESCRIPTION.includes(data.errorDescription);
            }

            if (data.messageType === 'editOrder') {
              const editIndex = result.value.data.items.findIndex(
                (s) => s.orderId === data.parentOrderId
              );

              if (editIndex > -1) {
                const item = result.value.data.items[editIndex];

                if (item.dateOfEvent) {
                  const resultTime = new Date(item.dateOfEvent).getTime();
                  const socketTime = new Date(data.dateOfEvent).getTime();

                  if (resultTime > socketTime) {
                    return;
                  }
                }

                Object.assign(result.value.data.items[editIndex], {
                  ...result.value.data.items[editIndex],
                  orderStateId: data.parentOrderStateId,
                  orderStateEnglishTitle: data.parentOrderState,
                  dateOfEvent: data.dateOfEvent,
                });
              }
            }

            const index = result.value.data.items.findIndex(
              (s) => s.orderId === data.orderId
            );

            if (index > -1) {
              const item = result.value.data.items[index];

              if (item.dateOfEvent) {
                const resultTime = new Date(item.dateOfEvent).getTime();
                const socketTime = new Date(data.dateOfEvent).getTime();

                if (resultTime > socketTime) {
                  return;
                }
              }

              result.value.data.items.splice(index, 1, {
                ...result.value?.data.items[index],
                ...data,
                lastTradePrice:
                  result.value?.data.items[index].lastTradePrice ??
                  data?.lastTradePrice,
              });
            } else {
              result.value.data.items.unshift(data);

              // Set the new order id to get the correct lastTradePrice
              if (data.messageType !== 'ErrorOrder') {
                sleep(500).then(() => {
                  orderId.value = data.orderId;
                });
              }
            }
          }
        }
      }
    },
  });

  const orders = computed(() => {
    if (!result.value?.data?.items?.length) {
      return [];
    }

    return result.value.data.items.filter((order) => {
      if (isNewOms.value) {
        if (
          order.orderStateId === NEW_OMS_ORDER_STATUSES.failed.OrderModified
        ) {
          return order.executedQuantity > 0;
        }

        return order.orderType !== ALGORITHM_ORDER_TYPE;
      } else {
        order.orderState = transformOrderLabel(order);

        if (order.orderStateId === LEGACY_ORDER_STATUSES.done.Modified) {
          return order.executedQuantity > 0;
        }

        return (
          order.orderStateId !==
            LEGACY_ORDER_STATUSES.failed.ModificationRejected &&
          order.orderStateId !== LEGACY_ORDER_STATUSES.open.ModifiedAndSent &&
          order.orderType !== ALGORITHM_ORDER_TYPE
        );
      }
    });
  });

  useLiveOrderQuery({
    orderId,
    orders: computed(() => result.value?.data?.items || []),
  });

  function addOrderToList(order: CreateOrderItem) {
    if (order) {
      const {
        accountRouteTypeId,
        canceledQuantity,
        instrumentIsin,
        instrumentName,
        investorBourseCode,
        investorBourseCodeId,
        investorTitle,
        lastPrice,
        orderEntryPersianDate,
        orderId,
        orderNumber,
        orderSide,
        orderSideId,
        orderState,
        orderStateEnglishTitle,
        orderStateId,
        parentExecutedQuantity,
        personageId,
        price,
        quantity,
        remainingQuantity,
        time,
        userName,
        userOrderEntryId,
        validityTypeTitle,
        entryDate,
      } = order;

      const newOrderItem: any = {
        accountRouteTypeId,
        canceledQuantity,
        instrumentIdentification: instrumentIsin,
        instrumentName,
        investorExchangeCode: investorBourseCode,
        investorExchangeCodeId: investorBourseCodeId,
        investorTitle,
        lastPrice,
        orderEntryPersianDate,
        orderId,
        orderNumber,
        orderSide,
        orderSideId,
        orderState,
        orderStateEnglishTitle,
        orderStateId,
        executedQuantity: parentExecutedQuantity,
        personageId,
        price,
        quantity,
        remainingQuantity,
        time,
        userName,
        userOrderEntryId,
        validityTypeTitle,
        entryDate,
      };

      result.value?.data?.items?.unshift(newOrderItem);
    }
  }

  function updateOrderStatus({ orderId, type }: UpdateOrderStatusParamsType) {
    if (result.value?.data?.items) {
      const index = result.value.data.items.findIndex(
        (s) => s.orderId === orderId
      );

      if (index > -1) {
        Object.assign(result.value.data.items[index], {
          orderStateId: LEGACY_ORDER_STATUSES.open[type],
          orderStateEnglishTitle: type,
        });
      }
    }
  }

  return {
    result,
    orders,
    isWaitingForOrders,
    isLoadingOrders,
    isErrorLoadingOrders,
    isSuccessLoadingOrders,
    isErrorOrderOnSocket,
    getOrders: execute,
    addOrderToList,
    updateOrderStatus,
  };
}
